import axios from 'axios'
import { camelise, snakify } from '@/utilities/functions'
import { prefixUrl } from '@/utilities/urls'
import store from '@/plugins/store'
import Vue from 'vue'
import Vuex from 'vuex'
import { config as env } from '@/plugins/env'

Vue.use(Vuex)

if (window.location.hostname.includes('bepartofresearch-uat')){
  env.API_ROOT_URL="https://bepartofresearch-api-uat.nihr.ac.uk"
}
if(window.location.hostname.includes('bepartofresearch-dev'))
{
  env.API_ROOT_URL="https://bepartofresearch-api-dev.nihr.ac.uk"
}

const { API_ROOT_URL, CAMELISE_INCOMING, SNAKIFY_OUTGOING } = env

function addApiHeaders(headers, { json = false } = {}) {
  const authType = store?.getters['core/authType']
  if (json) addJsonHeaders(headers)
  if (!authType) return addNoAuthHeaders(headers)
  else if (authType === 'passport') return addPassportHeaders(headers)
  else if (authType === 'oidc') return addOidcHeaders(headers)
  else throw 'authType not recognised'
}

function addBearerTokenHeader(headers, { accessToken } = {}) {
  if (accessToken) headers['Authorization'] = 'Bearer ' + accessToken
  return headers
}

function addJsonHeaders(headers) {
  headers['Content-Type'] = 'application/json'
  headers['Accept'] = 'application/json'
  return headers
}

function addNoAuthHeaders(headers) {
  return headers
}

function addOidcHeaders(headers) {
  headers['Accept'] = '*/*'
  return addBearerTokenHeader()
}

function addPassportHeaders(headers) {
  return addBearerTokenHeader(headers)
}

/**
 * A function for making api requests.
 *
 * @param {String} url Target api URL
 * @param {Object} options Optional parameters (optional)
 * @param {Boolean} options.auth Indicates if url requires authentication
 * @param {Mixed} options.data Request payload
 * @param {Object} options.headers Request headers (default: {})
 * @param {Object} options.json Convert body to json (default: false)
 * @param {String} options.method Request method (default: 'GET')
 * @param {String} options.prefix URL prefix (default: 'api')
 * @param {Object} options.params Query parameters (default: {})
 * @param {Object} axiosOptions Additional options passed directly through to axios (default: {})
 *
 * @returns {Mixed} The result of the api request
 */
export default async function api(url, options, axiosOptions) {
  return await request(url, options, axiosOptions)
}

function getBody(data, stringify) {
  if (!data) return ''
  if (typeof data === 'string') return data
  const body = SNAKIFY_OUTGOING === 'true' ? snakify(data) : data
  return stringify ? JSON.stringify(body) : body
}

function getUrl(prefix, url) {
  return prefixUrl(prefixUrl(url, prefix), API_ROOT_URL)
}

function onUndefinedError(e) {
  console.error('api error (undefined error)')
  if (typeof e !== 'undefined') console.error(e)
  // if nothing passed, assume for now 400 bad request
  else store.dispatch('core/onApiError', { status: 400 })
}

async function request(url, opts, axiosOptions = {}) {
  if (!url) throw 'api needs a valid target url'
  const { auth, data, json, headers = {}, method = 'GET', prefix = 'api', params = {} } = opts || {}
  store.commit('core/setApiEndpointsLoading', { method, url, value: true })
  const options = {
    data: getBody(data, json),
    headers: addApiHeaders(headers, opts),
    method,
    params: SNAKIFY_OUTGOING === 'true' ? snakify(params) : params,
    url: getUrl(prefix, url),
    // pass through additional options last to override previous options where necessary
    ...axiosOptions,
    withCredentials: !!auth || false,
  }
  return await axios(options)
    .then(r => {
      const c = CAMELISE_INCOMING === 'true' ? camelise(r) : r
      console.debug('result', c)
      return c
    })
    .catch(e => {
      const r = CAMELISE_INCOMING === 'true' ? camelise(e) : e
      if (!r?.response?.status) onUndefinedError(r?.response)
      else {
        console.error('api error', r)
        store.dispatch('core/onApiError', r?.response)
      }
    })
    .finally(() => {
      store.commit('core/setApiEndpointsLoading', { method, url, value: false })
    })
}
